import { type ReactNode, useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import { useMayRunShows } from "../auth/roles";
import { type CustomerDetails as Details, customers } from "./api";
import styles from "./Chat.module.scss";

/**
 * Who a commenter actually is (`R-MOD-1b`), and the host's mute in every show (`R-MOD-1a`).
 *
 * Opened by pressing a name in the feed. A display name is not unique, so the email is what tells
 * two *Sarah*s apart, and the mute at the bottom is placed under it on purpose: the one sanction
 * that outlives a show should be pressed by somebody who has just read whose it is.
 *
 * Both roles see the details; only a host sees the button, and the server is what enforces that
 * (`customer.ts`). Nothing here is cached — every opening reads Shopify again, because the
 * addresses are Shopify's and the platform keeps none (`R-ADDR-1`).
 */
export function CustomerDetails({
  accountId,
  showId,
  onClose,
}: {
  /** The account to show, or undefined when the popup is closed. */
  accountId: string | undefined;
  showId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Modal
      opened={accountId !== undefined}
      onClose={onClose}
      title={t("customer.title")}
      centered
      radius="lg"
    >
      {/* Keyed on the account, so opening somebody else starts from nothing rather than showing
          the last person's details while the next ones load. */}
      {accountId ? (
        <Body key={accountId} accountId={accountId} showId={showId} />
      ) : null}
    </Modal>
  );
}

function Body({ accountId, showId }: { accountId: string; showId: string }) {
  const { t } = useTranslation();
  const mayMute = useMayRunShows();
  const [details, setDetails] = useState<Details | undefined>(undefined);
  const [failed, setFailed] = useState<string | undefined>(undefined);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    customers.read(accountId, showId).then(
      (read) => {
        if (alive) setDetails(read);
      },
      (failure: unknown) => {
        if (alive) {
          setFailed(
            failure instanceof ApiError ? failure.message : t("chat.unreachable"),
          );
        }
      },
    );
    return () => {
      alive = false;
    };
  }, [accountId, showId, t]);

  async function act(mute: boolean) {
    setBusy(true);
    setFailed(undefined);
    setNote(undefined);
    try {
      const outcome = mute
        ? await customers.muteEverywhere(accountId)
        : await customers.unmuteEverywhere(accountId);
      // Reported rather than assumed, as for the per-show mute: without the disconnect the
      // change waits for their token to expire.
      setNote(outcome.disconnected ? undefined : t("customer.slowNote"));
      setDetails(await customers.read(accountId, showId));
    } catch (failure) {
      setFailed(failure instanceof ApiError ? failure.message : t("chat.unreachable"));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  const name = details?.account.displayName ?? t("chat.someone");

  return (
    <>
      {failed ? (
        <Alert color="red" variant="light" radius="md" mb="sm">
          {failed}
        </Alert>
      ) : null}

      {!details ? (
        failed ? null : (
          <Group justify="center" py="lg">
            <Loader size="sm" />
          </Group>
        )
      ) : (
        <Stack gap="md">
          <Stack gap={4}>
            <Text fw={650} size="lg" className={styles.body}>
              {name}
            </Text>
            <Field label={t("customer.email")}>
              <Anchor href={`mailto:${details.account.email}`} size="sm">
                {details.account.email}
              </Anchor>
            </Field>
            <Field label={t("customer.joined")}>
              <Text size="sm">
                {new Date(details.account.joinedAt).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </Text>
            </Field>
            {details.account.staff ? (
              <Badge size="sm" variant="light" w="fit-content">
                {t("customer.staff")}
              </Badge>
            ) : null}
          </Stack>

          <Stack gap={6}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              {t("customer.addresses")}
            </Text>
            <Addresses shopify={details.shopify} />
          </Stack>

          {details.shopify.customerUrl ? (
            <Anchor
              href={details.shopify.customerUrl}
              target="_blank"
              rel="noreferrer"
              size="sm"
            >
              {t("customer.openInShopify")}
            </Anchor>
          ) : null}

          {/* `R-MOD-1a`. Withheld from a moderator and against staff — the server refuses both. */}
          {mayMute && !details.account.staff ? (
            <>
              <Divider />
              {note ? (
                <Alert color="yellow" variant="light" radius="md">
                  {note}
                </Alert>
              ) : null}
              {details.chatMute ? (
                <Stack gap="xs">
                  <Text size="sm">
                    {t("customer.mutedSince", {
                      when: new Date(details.chatMute.mutedAt).toLocaleString("en-GB"),
                      by: details.chatMute.mutedByName ?? t("chat.someone"),
                    })}
                  </Text>
                  <Button
                    variant="default"
                    loading={busy}
                    onClick={() => void act(false)}
                  >
                    {t("customer.unmuteEverywhere")}
                  </Button>
                </Stack>
              ) : confirming ? (
                <Stack gap="xs">
                  <Text size="sm">{t("customer.confirmText", { name })}</Text>
                  <Group grow>
                    <Button color="red" loading={busy} onClick={() => void act(true)}>
                      {t("customer.confirm")}
                    </Button>
                    <Button variant="default" onClick={() => setConfirming(false)}>
                      {t("chat.cancel")}
                    </Button>
                  </Group>
                </Stack>
              ) : (
                <Button color="red" variant="light" onClick={() => setConfirming(true)}>
                  {t("customer.muteEverywhere")}
                </Button>
              )}
            </>
          ) : null}
        </Stack>
      )}
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Group gap="xs" wrap="nowrap" align="baseline">
      <Text size="sm" c="dimmed" className={styles.fieldLabel}>
        {label}
      </Text>
      {children}
    </Group>
  );
}

/** The addresses, or a line saying why there are none — which is not always "they have none". */
function Addresses({ shopify }: { shopify: Details["shopify"] }) {
  const { t } = useTranslation();

  if (shopify.status !== "ok") {
    return (
      <Text size="sm" c="dimmed">
        {t(`customer.shopify.${shopify.status}`)}
      </Text>
    );
  }
  if (shopify.addresses.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        {t("customer.noAddresses")}
      </Text>
    );
  }
  return (
    <Stack gap="xs">
      {shopify.addresses.map((address) => (
        <div key={address.id}>
          {address.formatted.map((line, index) => (
            <Text key={index} size="sm" lh={1.35}>
              {line}
            </Text>
          ))}
          {address.isDefault ? (
            <Badge size="xs" variant="light" mt={2}>
              {t("customer.default")}
            </Badge>
          ) : null}
        </div>
      ))}
    </Stack>
  );
}
